import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '../../../shared/services/api/authentication/authentication.service';
import { finalize, first } from 'rxjs/operators';

export type ResendState = 'idle' | 'sending' | 'sent';

@Component({
  selector: 'app-forgot-password-expired',
  templateUrl: './forgot-password-expired.component.html',
  styleUrls: ['./forgot-password-expired.component.scss'],
})
export class ForgotPasswordExpiredComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  loading = false;
  isError = false;
  submitted = false;
  isEmailSend = false;
  /** Button state: idle (default/cooldown done), sending, or just sent (green) */
  resendState: ResendState = 'idle';
  /** Cooldown seconds before allowing "Recevoir un nouveau lien" again */
  cooldown = 0;
  private cooldownInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      email: ['', [Validators.required]],
    });
  }

  ngOnDestroy(): void {
    if (this.cooldownInterval) {
      clearInterval(this.cooldownInterval);
    }
  }

  get f() {
    return this.form.controls;
  }

  /** True when button should show sent state (green "E-mail envoyé !") */
  get showSentState(): boolean {
    return this.resendState === 'sent';
  }

  /** True when button should show cooldown "Renvoyer (Xs)" disabled */
  get showCooldownState(): boolean {
    return this.cooldown > 0 && this.resendState === 'idle';
  }

  /** True when feedback line "Vérifiez votre boîte mail..." should show */
  get showCheckInboxHint(): boolean {
    return this.resendState === 'sent' || (this.cooldown > 0 && this.resendState === 'idle');
  }

  private startCooldown(): void {
    this.cooldown = 60;
    this.resendState = 'idle';
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
    this.cooldownInterval = setInterval(() => {
      this.cooldown -= 1;
      if (this.cooldown <= 0 && this.cooldownInterval) {
        clearInterval(this.cooldownInterval);
        this.cooldownInterval = null;
      }
    }, 1000);
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.form.invalid) {
      this.isError = true;
      return;
    }
    this.isError = false;
    this.loading = true;
    this.resendState = 'sending';

    this.authService
      .forgotPassword(this.f['email'].value.trim())
      .pipe(first())
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.isEmailSend = true;
          this.resendState = 'sent';
          setTimeout(() => this.startCooldown(), 2000);
        },
        error: () => {
          this.isError = true;
          this.resendState = 'idle';
        },
      });
  }
}
